import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import UserInput from '@wix/da-shared-react/pkg/Chat/UserInput';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { userSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/user';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';

import { useModuleConfig } from '../../../../utils/hooks/useModuleConfig';
import {
  recruitmentModuleInitialized,
  recruitmentModuleSaved,
} from '../../../actions/modules';

import s from './GroupChangeFounderModal.scss';

export interface Props {
  moduleId: number;
  requestClose: () => void;
}

const GroupChangeFounderModal: React.FC<Props> = ({
  moduleId,
  requestClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [user, setUser] = useState<PapiUser>();
  const inputId = useUniqueId();

  const {
    isSaving,
    isFetching,
    moduleConfig,
    fetchModuleConfig,
    saveResponse,
    saveModuleConfig,
    hasJustFinishedSaving,
    hasJustFinishedFetching,
    error,
  } = useModuleConfig(moduleId, '/founder');
  const { config, options } = moduleConfig || {};

  const handleSave = useCallback(() => {
    if (user) {
      saveModuleConfig({ founder_username: user.username });
    }
  }, [saveModuleConfig, user]);

  // fetch config
  useEffect(() => {
    if (!isFetching && !config && !error) {
      fetchModuleConfig();
    }
  }, [fetchModuleConfig, isFetching, config, error]);

  // got config, announce that we've initialized
  useEffect(() => {
    dispatch(recruitmentModuleInitialized());
  }, [dispatch, hasJustFinishedFetching]);

  useEffect(() => {
    if (!error && hasJustFinishedSaving) {
      if (saveResponse.thisGroup) {
        // update the group privs and meta in the store
        const { entities } = normalize(saveResponse.thisGroup, userSchema);
        dispatch(putEntities({ entities }));
      }
      // announce that we finished changing the founder
      dispatch(recruitmentModuleSaved());
    }
  }, [dispatch, hasJustFinishedSaving, error, saveResponse]);

  const candidates = options?.candidates;

  if (!config) {
    return null;
  }

  return (
    <Modal
      isOpen
      showFooterBorder
      showHeaderBorder
      heading={t('groups.leave.founder.popup.title')}
      onRequestClose={requestClose}
      footer={
        <div className={s['footer']}>
          <TextButton
            key="cancel"
            variant="main"
            size="large"
            onClick={requestClose}
          >
            {t('common.cancel')}
          </TextButton>
          <BrandedButton
            key="confirm"
            variant="brand-green"
            size="large"
            disabled={!user || isSaving}
            onClick={handleSave}
          >
            {t('common.confirm')}
          </BrandedButton>
        </div>
      }
    >
      <div className={s['root']}>
        <div className={s['body']}>{t('groups.leave.founder.popup.body')}</div>
        <FormElement
          inputId={inputId}
          label={
            <InputLabelText isRequired={true}>
              {t('groups.leave.founder.user.label')}
            </InputLabelText>
          }
        >
          <UserInput
            prefix={<SearchIcon size={IconSize.SMALL} />}
            users={user ? [user] : []}
            candidates={candidates}
            onAddUser={userToAdd => setUser(userToAdd)}
            onRemoveUser={_ => setUser(undefined)}
            onRemoveAll={() => setUser(undefined)}
          />
        </FormElement>
      </div>
    </Modal>
  );
};
GroupChangeFounderModal.displayName = 'GroupChangeFounderModal';

export default GroupChangeFounderModal;
